import { useEffect, useMemo, useState } from 'react'
import { mergeSuppliers, type SupplierListRow } from '../../shared/api/suppliers.api'
import { toast } from '../../shared/ui/toastStore'

// Конверт ошибок API — { success: false, error: { message } } (русские тексты).
const errText = (e: unknown): string => {
  const data = (e as { response?: { data?: { error?: { message?: string }; message?: string } } })
    ?.response?.data
  return data?.error?.message ?? data?.message ?? (e as Error)?.message ?? 'Ошибка'
}

// Объединение дублей поставщиков (WTR / Wtr / Wtron — одно юрлицо).
// Выбранные в таблице строки; главный — радиокнопкой (по умолчанию тот,
// у кого больше квот). Остальные становятся его алиасами: статистика
// объединяется, рекламации переезжают, квоты/заявки не меняются.
export function SupplierMergeModal({
  rows,
  onClose,
  onMerged,
}: {
  rows: SupplierListRow[]
  onClose: () => void
  onMerged: () => void
}) {
  const sorted = useMemo(() => [...rows].sort((a, b) => b.quotesGiven - a.quotesGiven), [rows])
  const [targetId, setTargetId] = useState<string>(sorted[0]?.id ?? '')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, submitting])

  const sources = sorted.filter((r) => r.id !== targetId)

  const submit = async () => {
    if (!targetId || sources.length === 0) return
    setSubmitting(true)
    try {
      await mergeSuppliers(
        targetId,
        sources.map((s) => s.id),
      )
      // Число, а не перечень имён: объединяют и сотни записей разом (А7.1).
      toast.success(`Объединено ${sources.length.toLocaleString('ru-RU')}`)
      onMerged()
    } catch (e) {
      toast.error(errText(e))
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">
            Объединить поставщиков ({sorted.length})
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Выберите главного — остальные станут его вторыми именами (алиасами). Статистика и
            рекламации объединятся; квоты и заявки не изменятся.
          </p>
        </div>

        <div className="flex-1 overflow-auto px-5 py-3">
          <ul className="space-y-1">
            {sorted.map((r) => (
              <li key={r.id}>
                <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="merge-target"
                    checked={targetId === r.id}
                    onChange={() => setTargetId(r.id)}
                    className="accent-brand-600"
                  />
                  <span className="text-sm text-gray-800 flex-1 truncate">{r.name}</span>
                  <span className="text-xs text-gray-400 shrink-0">
                    {r.quotesGiven.toLocaleString('ru-RU')} квот
                    {r.wonItems > 0 ? ` · ${r.wonItems.toLocaleString('ru-RU')} в счёт` : ''}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {sources.length > 0 && (
            <p className="mt-3 text-xs text-gray-500">
              Алиасами станут: {sources.map((s) => `«${s.name}»`).join(', ')}
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting || sources.length === 0}
            className="px-4 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? 'Объединяю…' : 'Объединить'}
          </button>
        </div>
      </div>
    </div>
  )
}
